import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { environment } from "@/config/env";
import { EliteStructureContext } from "@/contexts/EliteTemporaryContextProvider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

export interface PublicPolicyItem {
  privacyPolicy: string;
  marketingPolicy: string;
  termOfUse: string;
}

export interface PublicPrivacyResponse {
  structureId: number;
  structureName: string;
  language: string;
  privacyPolicy: string;
  marketingPolicy: string;
  termOfUse: string;
  translations: Record<string, PublicPolicyItem>;
}

const privacyCache = new Map<number, PublicPrivacyResponse>();

function normalizeLang(lang?: string): string {
  const clean = (lang || "it").trim().toLowerCase().split("-")[0];
  if (clean === "gb" || clean === "en") return "en";
  if (clean === "al" || clean === "sq") return "al";
  return "it";
}

export function useStructurePrivacy(explicitStructureId?: number | string | null) {
  const { i18n } = useTranslation();
  const structureCtx = useContext(EliteStructureContext);

  const resolvedStructureId = useMemo(() => {
    const raw =
      explicitStructureId ??
      structureCtx?.structure?.id ??
      structureCtx?.structure?.Id ??
      localStorage.getItem("structureId");
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : null;
  }, [explicitStructureId, structureCtx?.structure]);

  const [publicData, setPublicData] = useState<PublicPrivacyResponse | null>(() =>
    resolvedStructureId ? privacyCache.get(resolvedStructureId) ?? null : null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const lang = normalizeLang(i18n.language);

  useEffect(() => {
    if (!resolvedStructureId) return;

    const cached = privacyCache.get(resolvedStructureId);
    if (cached) {
      setPublicData(cached);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    axios
      .get<PublicPrivacyResponse>(
        `${environment.ELITE_BASE_URI}/public/privacy/${resolvedStructureId}`,
        { params: { lang } },
      )
      .then((res) => {
        if (!cancelled && res.data) {
          privacyCache.set(resolvedStructureId, res.data);
          setPublicData(res.data);
        }
      })
      .catch(() => {
        // Fallback to context translations if public endpoint fails
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedStructureId, lang]);

  const labelTranslations = structureCtx?.labelTranslationService;

  const privacyPolicyHtml = useMemo(() => {
    const fromPublic =
      publicData?.translations?.[lang]?.privacyPolicy ||
      publicData?.translations?.["it"]?.privacyPolicy ||
      publicData?.privacyPolicy;
    if (fromPublic) return fromPublic;

    const fromContext =
      labelTranslations?.[i18n.language]?.["structure.privacy.privacyPolicy"] ||
      labelTranslations?.[lang]?.["structure.privacy.privacyPolicy"] ||
      labelTranslations?.[i18n.language]?.["structure.hotspot.privacyPolicy"] ||
      labelTranslations?.[lang]?.["structure.hotspot.privacyPolicy"] ||
      labelTranslations?.["it"]?.["structure.privacy.privacyPolicy"] ||
      labelTranslations?.["it"]?.["structure.hotspot.privacyPolicy"] ||
      labelTranslations?.["Italian"]?.["structure.privacy.privacyPolicy"] ||
      labelTranslations?.["Italian"]?.["structure.hotspot.privacyPolicy"];

    return fromContext || "";
  }, [publicData, labelTranslations, lang, i18n.language]);

  const marketingPolicyHtml = useMemo(() => {
    const fromPublic =
      publicData?.translations?.[lang]?.marketingPolicy ||
      publicData?.translations?.["it"]?.marketingPolicy ||
      publicData?.marketingPolicy;
    if (fromPublic) return fromPublic;

    const fromContext =
      labelTranslations?.[i18n.language]?.["structure.hotspot.marketingPolicy"] ||
      labelTranslations?.[lang]?.["structure.hotspot.marketingPolicy"] ||
      labelTranslations?.["it"]?.["structure.hotspot.marketingPolicy"] ||
      labelTranslations?.["Italian"]?.["structure.hotspot.marketingPolicy"];

    return fromContext || privacyPolicyHtml || "";
  }, [publicData, labelTranslations, lang, i18n.language, privacyPolicyHtml]);

  const termOfUseHtml = useMemo(() => {
    const fromPublic =
      publicData?.translations?.[lang]?.termOfUse ||
      publicData?.translations?.["it"]?.termOfUse ||
      publicData?.termOfUse;
    if (fromPublic) return fromPublic;

    const fromContext =
      labelTranslations?.[i18n.language]?.["structure.hotspot.termOfUse"] ||
      labelTranslations?.[lang]?.["structure.hotspot.termOfUse"] ||
      labelTranslations?.["it"]?.["structure.hotspot.termOfUse"] ||
      labelTranslations?.["Italian"]?.["structure.hotspot.termOfUse"];

    return fromContext || "";
  }, [publicData, labelTranslations, lang, i18n.language]);

  return {
    structureId: resolvedStructureId,
    privacyPolicyHtml,
    marketingPolicyHtml,
    termOfUseHtml,
    isLoading,
  };
}

interface PrivacyPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  htmlContent?: string;
  fallbackText?: string;
  closeLabel?: string;
}

export function PrivacyPolicyDialog({
  open,
  onOpenChange,
  title = "Informativa sulla Privacy",
  htmlContent,
  fallbackText,
  closeLabel = "Chiudi",
}: PrivacyPolicyDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-6 overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Lock className="size-5 text-primary shrink-0" />
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1 min-h-0 max-h-[60vh] overflow-y-auto pr-4 my-2">
          {htmlContent ? (
            <div
              className="text-xs sm:text-sm text-foreground/90 space-y-2 leading-relaxed prose prose-sm dark:prose-invert max-w-none break-words"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          ) : (
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {fallbackText || ""}
            </p>
          )}
        </ScrollArea>
        <DialogFooter className="pt-2 border-t mt-auto">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {closeLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
